import { createRoot } from 'react-dom/client';
import { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { returnString } from './utils/environmentVariables';
import { datadogRum } from '@datadog/browser-rum';
// The package exposes /coverage through its `exports` map, which eslint-plugin-import's node
// resolver can't read; tsc and Vite resolve it fine.
// eslint-disable-next-line import/no-unresolved
import { setPermSink, setBuildDefault } from '@restore/permission-catalog/coverage';
import { App } from 'src/app';
import Intercom from './components/intercom';
import FullStory from './components/fullstory';
import { AuthProvider } from './routes/AuthContext';
import { Provider as ReduxProvider } from 'react-redux';
import { persistor, reduxStore } from './redux/store';
import { PersistGate as ReduxPersist } from 'redux-persist/lib/integration/react';
import { FullStory as FS, init as initFullStory } from '@fullstory/browser';
import { installClientIpFetch } from './services/clientIpService/installClientIpFetch';

// BRK-2181: before anything renders, so every fetch to our backends carries X-Client-IP.
installClientIpFetch();

const root = createRoot(document.getElementById('root') as HTMLElement);

datadogRum.init({
  applicationId: returnString(import.meta.env.VITE_DATADOG_PROVIDER_APP_ID),
  clientToken: returnString(import.meta.env.VITE_DATADOG_CLIENT_TOKEN),
  site: 'datadoghq.com',
  service: returnString(import.meta.env.VITE_DATADOG_APP_NAME),
  env: returnString(import.meta.env.VITE_ENVIRONMENT),
  version: returnString(import.meta.env.VITE_BUILD_VERSION, 'localhost'),
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  allowedTracingUrls: [
    /(https:|http:)*\/\/(localhost:3000|api\.(qa-|stage-|production-)*insights.restoreskills.com)/,
  ],
});

// Permission-coverage telemetry: resolve the build key in app code and push decisions to RUM as
// custom actions (rbac.permission.evaluated). Inert unless the build key (QA/stage) or a
// localStorage/URL override is on.
setBuildDefault(import.meta.env.VITE_PERM_TRACE_DEFAULT === 'on');
setPermSink((name, ctx) => datadogRum.addAction(name, ctx));

initFullStory({ orgId: 'o-21YY1Y-na1' });

FS('start');

root.render(
  <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <Suspense>
          <ReduxProvider store={reduxStore}>
            <ReduxPersist
              loading={null}
              persistor={persistor}
            >
              <App />
            </ReduxPersist>
            <Intercom />
            <FullStory />
          </ReduxProvider>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  </HelmetProvider>
);
